// QuestionBriefing.jsx
// Shown before each question — displays question number, specific instructions, then a Start button.
//
// Props:
//   questionNumber   : number   — 1, 2, 3, … (default 1)
//   subject          : string   — e.g. "Mathematics" (optional, shown as a tag)
//   timeLimit        : number   — seconds, e.g. 60 (optional, shown if provided)
//   marks            : number   — marks for this question (optional)
//   instructions     : string[] — array of instruction strings (default list used if omitted)
//   onStart          : () => void — called when Start button is clicked
//
// Integration — same as StudentCheckIn, drop into HomeworkScreen children:
//   <HomeworkScreen ...>
//     <QuestionBriefing
//       questionNumber={1}
//       subject="Mathematics"
//       timeLimit={90}
//       marks={5}
//       instructions={["Read the question twice before answering.", "Show your working where needed."]}
//       onStart={() => setPhase("question")}
//     />
//   </HomeworkScreen>

import { useState, useEffect } from "react";

// ── Ordinal helper ────────────────────────────────────────────────────────────
function ordinal(n) {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

// ── Icons ─────────────────────────────────────────────────────────────────────
const ArrowRightIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path d="M5 12h14M13 6l6 6-6 6" stroke="white" strokeWidth="2.2"
            strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const ClockIcon = ({ size = 16, color = "#F59E0B" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" stroke={color} strokeWidth="2" />
        <path d="M12 6v6l4 2" stroke={color} strokeWidth="2" strokeLinecap="round" />
    </svg>
);

const StarIcon = ({ size = 16, color = "#10B981" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
            fill={color} stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const CheckIcon = ({ color = "#6366F1" }) => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
        <path d="M20 6L9 17l-5-5" stroke={color} strokeWidth="2.5"
            strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const BookIcon = ({ size = 14, color = "#fff" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <path d="M4 19.5A2.5 2.5 0 016.5 17H20" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

// ── Default instructions ──────────────────────────────────────────────────────
const DEFAULT_INSTRUCTIONS = [
    "Read the question carefully before selecting your answer.",
    "You cannot go back once you move to the next question.",
    "Take a deep breath — trust what you know!",
];

// ── Main Component ────────────────────────────────────────────────────────────
export default function QuestionBriefing({
    questionNumber = 1,
    subject = null,
    timeLimit = null,
    marks = null,
    instructions = DEFAULT_INSTRUCTIONS,
    onStart,
}) {
    const [mounted, setMounted] = useState(false);
    const [starting, setStarting] = useState(false);

    useEffect(() => { setTimeout(() => setMounted(true), 50); }, []);

    const handleStart = () => {
        setStarting(true);
        setTimeout(() => onStart?.(), 350);
    };

    const ordinalLabel = ordinal(questionNumber);

    return (
        <div style={s.root}>

            {/* ── Top: question number badge + meta tags ── */}
            <div style={{
                ...s.header,
                opacity: mounted ? 1 : 0,
                transform: mounted ? "translateY(0)" : "translateY(-12px)",
                transition: "opacity 0.5s ease 0.05s, transform 0.5s ease 0.05s",
            }}>
                {/* Big ordinal label */}
                <div style={s.questionLabel}>
                    <div style={s.questionTag}>
                        <BookIcon size={12} color="#fff" />
                        Question
                    </div>
                    <h2 style={s.questionNumber}>
                        Ready for your <span style={s.highlight}>{ordinalLabel}</span> question?
                    </h2>
                </div>

                {/* Meta chips */}
                <div style={s.chips}>
                    {subject && (
                        <div style={{ ...s.chip, background: "#EDE9FE", color: "#6D28D9" }}>
                            📚 {subject}
                        </div>
                    )}
                    {timeLimit && (
                        <div style={{ ...s.chip, background: "#FEF3C7", color: "#92400E" }}>
                            <ClockIcon size={13} color="#D97706" />
                            {timeLimit}s
                        </div>
                    )}
                    {marks && (
                        <div style={{ ...s.chip, background: "#D1FAE5", color: "#065F46" }}>
                            <StarIcon size={13} color="#10B981" />
                            {marks} {marks === 1 ? "mark" : "marks"}
                        </div>
                    )}
                </div>
            </div>

            {/* ── Divider ── */}
            <div style={{
                ...s.divider,
                opacity: mounted ? 1 : 0,
                transition: "opacity 0.4s ease 0.2s",
            }} />

            {/* ── Instructions list ── */}
            <div style={s.instrBlock}>
                <div style={{
                    ...s.instrHeading,
                    opacity: mounted ? 1 : 0,
                    transform: mounted ? "translateY(0)" : "translateY(8px)",
                    transition: "opacity 0.5s ease 0.2s, transform 0.5s ease 0.2s",
                }}>
                    Before you begin
                </div>
                <div style={s.instrList}>
                    {instructions.map((instr, i) => (
                        <div
                            key={i}
                            style={{
                                ...s.instrItem,
                                opacity: mounted ? 1 : 0,
                                transform: mounted ? "translateX(0)" : "translateX(-10px)",
                                transition: `opacity 0.5s ease ${0.28 + i * 0.08}s, transform 0.5s ease ${0.28 + i * 0.08}s`,
                            }}
                        >
                            <div style={s.instrCheck}>
                                <CheckIcon color="#6366F1" />
                            </div>
                            <span style={s.instrText}>{instr}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* ── Start button ── */}
            <button
                style={{
                    ...s.startBtn,
                    opacity: mounted ? 1 : 0,
                    transform: mounted
                        ? (starting ? "scale(0.96)" : "scale(1)")
                        : "translateY(10px)",
                    transition: mounted
                        ? "transform 0.15s ease, box-shadow 0.2s ease"
                        : "opacity 0.5s ease 0.45s, transform 0.5s ease 0.45s",
                }}
                onClick={handleStart}
                disabled={starting}
            >
                <ArrowRightIcon />
                {starting ? "Loading question…" : `Start ${ordinalLabel} Question`}
            </button>

        </div>
    );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const s = {
    root: {
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        width: "100%",
        height: "100%",
        justifyContent: "center",
    },

    // Header
    header: {
        display: "flex",
        flexDirection: "column",
        gap: "10px",
    },
    questionLabel: {
        display: "flex",
        flexDirection: "column",
        gap: "6px",
    },
    questionTag: {
        display: "inline-flex",
        alignItems: "center",
        gap: "5px",
        background: "#6366F1",
        color: "#fff",
        borderRadius: "999px",
        padding: "3px 10px",
        fontSize: "11px",
        fontWeight: 800,
        fontFamily: "'Nunito', sans-serif",
        letterSpacing: "0.04em",
        width: "fit-content",
        textTransform: "uppercase",
    },
    questionNumber: {
        fontFamily: "'Playfair Display', serif",
        fontWeight: 900,
        fontSize: "clamp(18px, 3vw, 24px)",
        color: "#111",
        margin: 0,
        lineHeight: 1.2,
    },
    highlight: {
        color: "#F59E0B",
        fontStyle: "italic",
    },

    // Meta chips
    chips: {
        display: "flex",
        gap: "8px",
        flexWrap: "wrap",
    },
    chip: {
        display: "inline-flex",
        alignItems: "center",
        gap: "5px",
        borderRadius: "999px",
        padding: "4px 12px",
        fontSize: "12px",
        fontWeight: 700,
        fontFamily: "'Nunito', sans-serif",
    },

    // Divider
    divider: {
        width: "100%",
        height: "1.5px",
        background: "#F0E8C8",
        borderRadius: "2px",
        flexShrink: 0,
    },

    // Instructions
    instrBlock: {
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        flex: 1,
    },
    instrHeading: {
        fontFamily: "'Nunito', sans-serif",
        fontWeight: 700,
        fontSize: "11px",
        color: "#999",
        letterSpacing: "0.08em",
        textTransform: "uppercase",
    },
    instrList: {
        display: "flex",
        flexDirection: "column",
        gap: "8px",
    },
    instrItem: {
        display: "flex",
        alignItems: "flex-start",
        gap: "10px",
    },
    instrCheck: {
        width: "22px",
        height: "22px",
        borderRadius: "50%",
        background: "#EEF2FF",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        marginTop: "1px",
    },
    instrText: {
        fontFamily: "'Nunito', sans-serif",
        fontWeight: 600,
        fontSize: "13px",
        color: "#444",
        lineHeight: 1.5,
    },

    // Start button
    startBtn: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "8px",
        background: "linear-gradient(135deg, #FBBF24 0%, #F59E0B 100%)",
        color: "#fff",
        border: "none",
        borderRadius: "14px",
        padding: "14px 20px",
        fontSize: "15px",
        fontWeight: 800,
        fontFamily: "'Nunito', sans-serif",
        cursor: "pointer",
        letterSpacing: "0.01em",
        boxShadow: "0 4px 16px rgba(245,158,11,0.35)",
        marginTop: "4px",
        flexShrink: 0,
    },
};